<template>
  <main class="cashbox-manual">
    <modal-dialog
      v-bind:title="modalDialogTransactionTitle"
      v-model="isShowCashboxTransactionDialog"
      with-cross
    >
      <form
        v-on:submit.prevent="onSubmitTransaction"
      >
        <input-number
          v-bind:title="'Сумма'"
          v-bind:min="'0'"
          v-bind:name="'amount'"
          v-bind:required="true"
          v-model="amount"
          v-bind:withoutNativeControls="true"
        />
        <textarea-length-limitation
          class="cashbox-manual__description"
          v-bind:title="`Примечание`"
          v-bind:max-length="200"
          v-model="note"
          v-letter-capital
          rows="3"
        />
        <button
          class="cashbox-manual__submit"
          type="submit"
        >Подтвердить</button>
      </form>
    </modal-dialog>
    <div class="cashbox-manual__header">
      <h1>Касса</h1>
      <input-select
        option-title-key="name"
        option-value-key="id"
        title=""
        input-class="cashbox-manual__header__input-cashbox"
        v-bind:options="cashboxList"
        v-bind:value="selectedCashboxId"
        @input="onInputCashbox"
      />
      <div
        v-if="cashboxDataLoaded"
        class="cashbox-manual__header__balance header-balance">
        <p>
          <span class="header-balance__text">
            Текущий баланс (наличные средства):
          </span>
          <span>{{ cashboxCashBalance }}</span>
        </p>
        <p>
          <span class="header-balance__text">
            Текущий баланс (безналичные средства):
          </span>
          <span>{{ cashboxCashlessBalance }}</span>
        </p>
      </div>
      <date-range-with-presets
        v-bind:presets="periodPresets"
        v-if="periodDates.length"
        v-model="periodDates"
        v-on:input="onChangePeriod" />
      <div class="cashbox-manual__header__buttons">
        <button
          class="cashbox-manual__header__button button-entry"
          type="button"
          v-on:click="depositFunds"
        >Внесение</button>
        <button
          class="cashbox-manual__header__button button-removal"
          type="button"
          v-on:click="withdrawFunds"
        >Изъятие</button>
        <button
          type="button"
          v-on:click="fetchReportSpreadsheet"
          class="cashbox-manual__header__button-excel"
        >EXCEL</button>
      </div>
    </div>
    <div class="cashbox-manual__table-wrapper">
      <em v-if="isFetchingForOperations">Выполняется получение операций...</em>
      <strong v-else-if="hasNoOperations">Отсутствуют операции по счетам</strong>
      <table
        class="cashbox-manual__table cashbox-manual-table"
        v-else
      >
        <thead class="cashbox-manual-table__head">
          <tr>
            <th
              class="cashbox-manual-table__head-cell"
              v-bind:key="`cashbox-columns-${columnIndex}`"
              v-for="(title, columnIndex) in columns"
            >{{ title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="cashbox-manual-table__body-line"
            v-bind:key="lineKey"
            v-for="{ cells, expanded, lineKey } in bodyLines"
          >
            <td
              class="cashbox-manual-table__body-cell"
              v-bind:key="cellKey"
              v-for="{
                cellKey,
                hasShowMore,
                title,
                to,
              } in cells"
            >
              <template v-if="hasShowMore">
                <label
                  class="show-more-cell"
                  title="Показать за день"
                  v-bind:class="{ expanded }"
                >
                  <span class="show-more-cell__icon" />
                  <span>{{ title }}</span>
                  <input
                    class="show-more-cell__mark"
                    type="checkbox"
                    v-bind:value="lineKey"
                    v-model="expandedGroups">
                </label>
              </template>
              <router-link
                class="cashbox-manual-table__link"
                v-bind:to="to"
                v-else-if="to"
              >{{ title }}</router-link>
              <span v-else>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { convertToInputDate, formatDateString } from '@/utils/timeFormat';
import ModalDialog from '@/components/ModalDialog.vue';
import InputNumber from '@/components/InputNumber.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import InputSelect from '@/components/InputSelect.vue';

const makeGroupKey = (itemIndex) => (lineIndex) => `cashbox-lines:${itemIndex}:${lineIndex}`;

const mapLinkToLine = (linkTo) => {
  const [section, id, invoiceId] = linkTo.split(',', 3);
  if (section === 'invoices') {
    const to = (invoiceId !== undefined)
      ? {
        name: 'ClinicPanelPatientInvoice',
        params: {
          id,
          invoiceId,
        },
      }
      : {
        name: 'ClinicPanelPatientInvoiceList',
        params: {
          id,
        },
      };
    return (index) => ((index === 3) ? to : null);
  }
  return () => null;
};

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Раздел кассы без провайдера.
 */
export default {
  components: {
    TextareaLengthLimitation,
    ModalDialog,
    DateRangeWithPresets,
    InputNumber,
    InputSelect,
  },
  computed: {
    /**
     * @description Заголовок модального окна операций.
     * @return {string}
     */
    modalDialogTransactionTitle() {
      return this.isOut ? 'Изъятие суммы' : 'Внесение суммы';
    },
    /**
     * @description Строки таблицы.
     */
    bodyLines() {
      return this.items.flatMap((item, itemIndex) => {
        const groupKeyPrefix = makeGroupKey(itemIndex);
        const groupKey = groupKeyPrefix('head');
        const groupCells = [
          item.title,
          ...Array.from('-'.repeat(this.columns.length - 1)),
        ].fill('-', 1, this.columns.length).map(
          (title, groupCellIndex) => {
            const cellKey = `${groupKey}:${groupCellIndex}`;
            return {
              cellKey,
              hasShowMore: groupCellIndex === 0,
              title,
              to: null,
            };
          },
        );
        const expanded = this.expandedGroups.includes(groupKey);
        const lines = (expanded ? item.lines : []).flatMap((line, lineIndex) => {
          const lineKey = groupKeyPrefix(lineIndex);
          const lineLinkTo = mapLinkToLine(line.link_to);
          const lineCells = line.columns.map((title, cellIndex) => {
            const cellKey = `${lineKey}:${cellIndex}`;
            return {
              cellKey,
              hasShowMore: false,
              title,
              to: lineLinkTo(cellIndex),
            };
          });
          return { lineKey, cells: lineCells };
        });
        return [{ expanded, lineKey: groupKey, cells: groupCells }, ...lines];
      });
    },
    /**
     * @description Не имеет операций.
     */
    hasNoOperations() {
      return this.items.length === 0;
    },
    /**
     * @description Предварительные установки периодов.
     * @returns {({toValue: function(): [Date,Date], id: string, label: string, title: string}[])}
     */
    periodPresets() {
      return this.periodTypes.map(({ fromTo: [from, to], title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => [new Date(from), new Date(to)],
      }));
    },
  },
  created() {
    this.fetchOptions().then(() => {
      this
        .periodPresets
        .slice(0, 1)
        .map(({ toValue }) => toValue())
        .forEach((value) => { this.periodDates = value; });
      this.fetchOperations(this.selectedCashboxId);
    });
  },
  data() {
    return {
      /**
       * @description Столбцы операций.
       * @type {string[]}
       */
      columns: [],
      /**
       * @description Раскрытые группы.
       * @type {string[]}
       */
      expandedGroups: [],
      /**
       * @description Ожидается получение операций.
       * @type {boolean}
       */
      isFetchingForOperations: true,
      /**
       * @description Группы операций.
       * @type {({lines: {columns: string[][]}[], title: string}[])}
       */
      items: [],
      /**
       * @description Выбранные период дней.
       * @type {([Date, Date])}
       */
      periodDates: [],
      /**
       * @description Типы периодов.
       * @type {({fromTo: string[], title: string, value: string}[])}
       */
      periodTypes: [],
      /**
       * @description Показать окно операций пополнения/изъятия средств кассы.
       * @type {boolean}
       */
      isShowCashboxTransactionDialog: false,
      /**
       * @description Сумма.
       * @type {number}
       */
      amount: 0,
      /**
       * @description Операция снятия.
       * @type {boolean}
       */
      isOut: false,
      /**
       * @description Примечание операции.
       * @type {string}
       */
      note: '',
      cashboxDataLoaded: false,
      cashboxCashBalance: '',
      cashboxCashlessBalance: '',
    };
  },
  methods: {
    /**
     * @description Внести средства.
     * @returns {void}
     */
    depositFunds() {
      this.isOut = false;
      this.isShowCashboxTransactionDialog = true;
    },
    fetchCashboxBalance(cashboxId) {
      this
        .$cashboxGateway
        .viewCashboxBalance(cashboxId)
        .then(
          (result) => {
            result.forEachLeft(({ title }) => { this.notifyFail(title); });
            result.forEach(
              ({
                cashBalance,
                cashlessBalance,
              }) => {
                this.cashboxCashBalance = cashBalance;
                this.cashboxCashlessBalance = cashlessBalance;
              },
            );
          },
        )
        .finally(() => { this.cashboxDataLoaded = true; });
    },
    /**
     * @description Получить операции.
     * @param {number} cashboxId Идентификатор кассы.
     * @returns {void}
     */
    fetchOperations(cashboxId) {
      const dates = this.periodDates.map(convertToInputDate);
      this.isFetchingForOperations = true;
      this
        .$cashboxGateway
        .viewOperationsAllPatientInvoicesForPeriod(cashboxId, ...dates)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ columns, items }) => {
            this.columns = columns;
            this.items = items;
            this.expandedGroups = (this.items.length === 1)
              ? [makeGroupKey(0)('head')]
              : [];
            this.fetchCashboxBalance(this.selectedCashboxId);
          });
        })
        .finally(() => { this.isFetchingForOperations = false; });
    },
    /**
     * @description Получить опции.
     * @returns {Promise<void>}
     */
    fetchOptions() {
      return this
        .$cashboxGateway
        .viewInvoiceOperationOptions()
        .then((result) => {
          result.forEach(({ periodTypes }) => {
            this.periodTypes = periodTypes;
          });
        });
    },
    /**
     * @description Получить операции всех счетов пациентов за период в виде таблицы.
     * @returns {void}
     */
    fetchReportSpreadsheet() {
      const [dateFrom, dateTo] = this.periodDates.map(formatDateString);
      const onSuccess = (response) => {
        saveFile(response, `Отчет по кассе ${dateFrom} - ${dateTo}.xls`);
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const dates = this.periodDates.map(convertToInputDate);
      const cashboxId = this.selectedCashboxId;
      this
        .$cashboxGateway
        .viewOperationsAllPatientInvoicesForPeriodBySheet(cashboxId, ...dates)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать смену периода.
     * @returns {void}
     */
    onChangePeriod() {
      this.fetchOperations(this.selectedCashboxId);
    },
    /**
     * @description Изъять средства.
     * @returns {void}
     */
    withdrawFunds() {
      this.isOut = true;
      this.isShowCashboxTransactionDialog = true;
    },
    /**
     * @description Провести операцию по средствам.
     * @returns {Promise<void>}
     */
    onSubmitTransaction() {
      const command = this.isOut ? 'out' : 'in';
      const amountToKop = this.amount * 100;
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const onSuccess = (result) => {
        this.notifySuccess(result.title);
      };
      return this
        .$cashboxGateway
        .cashInOut(this.selectedCashboxId, command, amountToKop, this.note)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => {
          this.amount = 0;
          this.note = '';
          this.isShowCashboxTransactionDialog = false;
          this.fetchOperations(this.selectedCashboxId);
          this.fetchCashboxBalance(this.selectedCashboxId);
        });
    },
    onInputCashbox(cashboxId) {
      this.$emit('input-cashbox', cashboxId);
    },
  },
  mixins: [notifierMixin],
  name: 'ClinicPanelCashboxManual',
  props: {
    cashboxList: { required: true, type: Array },
    selectedCashboxId: { required: true, type: Number },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.cashbox-manual {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: auto;
  padding: 1.25rem 1rem;
  &__header {
    display: grid;
    gap: 20px;
    width: 100%;
    padding: 20px 20px 20px 20px;
    &__balance {
      display: grid;
      gap: 10px;
      p {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin: 0;
      }
    }
    .header-balance {
      &__text {
        display: flex;
        align-items: center;
        column-gap: 5px;
        width: min(100%, 330px);
      }
    }
    &__buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    &__button {
      @include buttons.btn-blue(210px, 48px, 16px);
      gap: 6px;
      &:is(.button-entry, .button-removal) {
        &::before {
          content: '';
          flex-shrink: 0;
          width: 18px;
          height: 19px;
        }
      }
      &.button-entry {
        &::before {
          mask: url("@/assets/img/plus.svg") no-repeat 50%;
          mask-size: 18px 19px;
          background-color: #ffffff;
        }
      }
      &.button-removal {
        &::before {
          background: url("@/assets/img/minus-rounded.svg") no-repeat 50%;
          background-size: 18px 19px;
        }
      }
    }
    &__button-excel {
      @include buttons.btn-transparent(210px, 48px, 16px);
      &:before {
        content: "";
        background-image: url("@/assets/img/exel.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 16px;
        height: 16px;
        margin: 0 8px 0 0;
      }
    }
    &__input-cashbox {
      margin-top: 0;
      padding-top: 0;
      max-width: 505px;
      margin-bottom: 15px;
    }
  }
  &__description {
    margin-top: 5px;
    .textarea-length-limitation__textarea {
      min-height: 80px;
      &:focus-visible {
        background-color: var(--primary-background-color);
        border: 2px solid var(--action-color);
        color: var(--reading-color);
        outline: 0;
      }
    }
  }
  &__submit {
    margin-top: 20px;
    margin-inline: auto;
    @include buttons.btn-blue(210px, 48px, 16px);
  }
  &__table-wrapper {
    overflow-x: auto;
  }
}

.cashbox-manual,
.cashbox-manual .cashbox-manual__table {
  width: 100%;
}

.cashbox-manual-table {
  min-width: 700px;
  border-collapse: collapse;
  font-size: .875rem;
  line-height: 1rem;
  overflow: auto;
  table-layout: fixed;
}

.cashbox-manual-table .cashbox-manual-table__body-cell {
  padding: 15px 8px;
  text-align: left;
  text-transform: none;
  white-space: pre-line;
}

.cashbox-manual-table .cashbox-manual-table__body-line {
  border-bottom: 1px solid var(--fill-color);
}

.cashbox-manual-table .cashbox-manual-table__head {
  border-bottom: 2px solid var(--action-color);
  font-weight: 700;
}

.cashbox-manual-table .cashbox-manual-table__head-cell {
  padding: 10px 8px;
  text-align: left;
}

.cashbox-manual-table .cashbox-manual-table__link:hover {
  text-decoration: none;
}

.show-more-cell {
  position: relative;
}

.show-more-cell .show-more-cell__mark {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.show-more-cell .show-more-cell__icon {
  align-items: center;
  background-image: url("@/assets/img/checked/arrow-top-blue.svg");
  background-position: bottom;
  background-size: contain;
  cursor: pointer;
  display: inline-flex;
  height: 15px;
  margin: 0 17px 0 0;
  user-select: none;
  width: 15px;
}

.show-more-cell.expanded .show-more-cell__icon {
  background-position: top;
  transform: rotate(180deg);
}
</style>
